import { transition, trigger, useAnimation } from '@angular/animations';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { getClassroom } from '@app/classrooms/_store/classroom/classroom.selectors';
import { moveFadeIn, moveFadeOut } from '@core/animations';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import { AccountService, ContentService, ContextService } from '@core/services';
import { IContentBlock } from '@core/services/interfaces';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { getMe } from '@store/reducers/profile.reducer';
import { Classroom, Content, FindContentStrategyEnum, IContentPane, IFindContentFilter } from 'lingo2-models';
import { takeUntil, tap } from 'rxjs/operators';
import {loadMe} from "@store/actions/profile.actions";
import {ErrorNotificationService} from "@shared/error-notification/error-notification.service";

type ContentBlock = IContentBlock<Content, IFindContentFilter>;

@Component({
  selector: 'app-classroom-content-page',
  templateUrl: './classroom-content-page.component.html',
  styleUrls: ['./classroom-content-page.component.scss'],
  animations: [
    trigger('meeting', [
      transition(':enter', useAnimation(moveFadeIn, { params: { time: '200ms', slide: '5px' } })),
      transition(':leave', useAnimation(moveFadeOut)),
    ]),
  ],
})
export class ClassroomContentPageComponent extends ChangableComponent implements OnInit {
  public classroom: Classroom;
  public isTeacher: boolean;
  public content: ContentBlock = {
    items: [],
    loading: false,
    loaded: false,
    filter: {
      use: FindContentStrategyEnum.classroom,
      classroom_id: null, // ниже переопределяется
    },
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
    },
  };

  constructor(
    public errorNotificationService: ErrorNotificationService,
    protected contentService: ContentService,
    private contextService: ContextService,
    protected loadingBar: LoadingBarService,
    private store: Store,
    protected cdr: ChangeDetectorRef,
  ) {
    super(cdr);
  }

  ngOnInit() {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.isTeacher = AccountService.isTeacher(me);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        }
      });

    this.store
      .select(getClassroom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (classroom) => {
          this.classroom = classroom;
          this.content.filter.classroom_id = this.classroom.id;
          this.loadContent();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        }
      });
  }

  protected loadContent(nextPage?: boolean) {
    if (this.content.loading) {
      return;
    }

    this.content.loading = true;
    this.loadingBar.fetchingStart(35);
    if (nextPage && this.content.pagination.page < this.content.pagination.totalPages) {
      this.content.pagination.page = this.content.pagination.page + 1;
    }

    this.contentService
      .getContent(this.content.filter, this.content.pagination, false)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (nextPage) {
            this.content.items = [...this.content.items, ...response.results];
          } else {
            this.content.items = response.results;
          }
          this.content.pagination = response.pagination;
          this.content.loading = false;
          this.content.loaded = true;
          this.loadingBar.fetchingComplete();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.content.loading = false;
          this.content.loaded = true;
        }
      });
  }

  public get noPublicationsMessage(): string {
    return this.isTeacher && this.classroom?.visit_info?.is_author
      ? 'classrooms.classroom-page.content.stubs.description'
      : 'classrooms.classroom-page.content.stubs.description-student';
  }

  // функция для трекинга списка, чтобы он не 'моргал'
  public trackContentByFn(index, item: Content): string {
    return item.id;
  }

  public onContentPosted() {
    this.reloadContent();
  }

  public reloadContent() {
    this.content.pagination.page = 1;
    this.loadContent();
  }

  public nextPage() {
    this.loadContent(true);
  }

  public trackByFn(index: number, item: IContentPane): number {
    return item.order;
  }
}
